import clsx from "clsx";
import type { Item } from "@/types/api";

type Placement = "card" | "detail";

interface RatingInput {
  score: number;
  votes?: number | null;
  source?: string | null;
  fromSeries?: boolean;
}

interface RatingBadgeProps {
  rating?: RatingInput | null;
  item?: Item;
  placement?: Placement;
}

export function RatingBadge({ rating, item, placement = "card" }: RatingBadgeProps) {
  const resolved = rating ?? (item ? resolveItemRating(item) : null);
  if (!resolved || resolved.score <= 0) return null;

  const percent = Math.round(resolved.score * 10);
  const tier = scoreTier(percent);
  const sourceLabel = formatSourceLabel(resolved.source ?? "tmdb");
  const titleParts = [`${sourceLabel} ${resolved.score.toFixed(1)} / 10`];
  if (resolved.votes && resolved.votes > 0) {
    titleParts.push(`${resolved.votes.toLocaleString()} votes`);
  }
  if (resolved.fromSeries) {
    titleParts.push("series rating");
  }

  return (
    <span
      className={clsx(
        "rating-badge",
        `rating-badge--${placement}`,
        `rating-badge--${tier}`,
        resolved.fromSeries && "rating-badge--inherited",
      )}
      title={titleParts.join(" • ")}
      aria-label={titleParts.join(", ")}
    >
      <span className="rating-badge-score">{percent}%</span>
      <span className="rating-badge-source">{sourceLabel}</span>
    </span>
  );
}

export function resolveItemRating(item: Item): RatingInput | null {
  const { rating_average, rating_votes, rating_source, series_rating_average, series_rating_votes, item_type } = item.attributes;

  if (typeof rating_average === "number" && rating_average > 0) {
    return {
      score: rating_average,
      votes: typeof rating_votes === "number" ? rating_votes : null,
      source: rating_source ?? null,
      fromSeries: false,
    };
  }

  if ((item_type === "episode" || item_type === "season") &&
      typeof series_rating_average === "number" && series_rating_average > 0) {
    return {
      score: series_rating_average,
      votes: typeof series_rating_votes === "number" ? series_rating_votes : null,
      source: rating_source ?? null,
      fromSeries: true,
    };
  }

  return null;
}

function scoreTier(percent: number): "high" | "mid" | "low" {
  if (percent >= 70) return "high";
  if (percent >= 50) return "mid";
  return "low";
}

function formatSourceLabel(source: string): string {
  switch (source.toLowerCase()) {
    case "tmdb":
      return "TMDb";
    case "imdb":
      return "IMDb";
    default:
      return source.toUpperCase();
  }
}
